import React from 'react';
import { Link } from 'react-router-dom';
import {
    Rocket,
    Shield,
    Satellite,
    Radio,
    Server,
    Globe,
    ArrowRight,
    CheckCircle2,
    Orbit,
    Zap,
    Clock,
    Eye
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernSpaceForce = () => {
    const products = [
        {
            icon: <Satellite size={28} />,
            title: 'Satellite Ground Systems',
            description: 'Advanced ground control stations for satellite operations and management.',
            badge: 'Space-certified',
            features: ['Telemetry processing', 'Command uplink', 'Orbit control', 'Health monitoring']
        },
        {
            icon: <Radio size={28} />,
            title: 'Space Communications',
            description: 'Secure space-to-ground and inter-satellite communication systems.',
            badge: 'Operational',
            features: ['Deep space comms', 'Laser links', 'Encrypted channels', 'High bandwidth']
        },
        {
            icon: <Eye size={28} />,
            title: 'Space Domain Awareness',
            description: 'Systems for tracking and monitoring objects in Earth orbit.',
            badge: 'Real-time',
            features: ['Object tracking', 'Collision avoidance', 'Threat detection', 'Space weather']
        },
        {
            icon: <Server size={28} />,
            title: 'Mission Control Infrastructure',
            description: 'Complete IT infrastructure for space operations centers.',
            badge: 'Mission-critical',
            features: ['Redundant systems', 'Real-time processing', 'Data fusion', 'Visualization']
        }
    ];

    const capabilities = [
        {
            icon: <Orbit size={28} />,
            metric: '1000+',
            title: 'Assets Tracked',
            description: 'Space objects monitored daily'
        },
        {
            icon: <Globe size={28} />,
            metric: 'Global',
            title: 'Coverage',
            description: 'Worldwide ground station network'
        },
        {
            icon: <Zap size={28} />,
            metric: 'Real-time',
            title: 'Data Processing',
            description: 'Instant telemetry analysis'
        },
        {
            icon: <Clock size={28} />,
            metric: '99.999%',
            title: 'Availability',
            description: 'Critical system uptime'
        }
    ];

    const features = [
        { icon: <Rocket size={24} />, title: 'Space', text: 'Orbital operations' },
        { icon: <Shield size={24} />, title: 'Secure', text: 'Classified systems' },
        { icon: <Satellite size={24} />, title: 'Connected', text: 'Global reach' },
        { icon: <Eye size={24} />, title: 'Aware', text: 'Space surveillance' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Space Force Solutions"
                subtitle="Technology for space superiority"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Department of Defense', path: '/department' },
                    { label: 'Space Force Solutions' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid jk-military-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Rocket size={16} />
                                U.S. Space Force Technology Partner
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Advanced Technology for
                                <span className="highlight"> Space Operations</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We deliver cutting-edge technology solutions for Space Force operations.
                                From satellite ground systems to space domain awareness, our solutions
                                enable space superiority and protect critical national assets.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Deploy Solutions
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/department" className="jk-btn jk-btn--secondary">
                                    DoD Overview
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Satellite size={16} />
                            Space Solutions
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Mission-Critical
                            <span className="highlight"> Space Technology</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            Systems designed for the unique challenges of space operations
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                        {products.map((product, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-header">
                                    <div className="jk-solutions-services__card-icon">
                                        {product.icon}
                                    </div>
                                    <span className="jk-solutions-services__card-badge">{product.badge}</span>
                                </div>
                                <h3 className="jk-solutions-services__card-title">{product.title}</h3>
                                <p className="jk-solutions-services__card-description">{product.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {product.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-solutions-capabilities">
                <div className="jk-solutions-capabilities__container">
                    <div className="jk-solutions-capabilities__header">
                        <h2 className="jk-solutions-capabilities__title">
                            Space Operations
                            <span className="highlight"> Excellence</span>
                        </h2>
                    </div>

                    <div className="jk-solutions-capabilities__grid">
                        {capabilities.map((capability, index) => (
                            <div key={index} className="jk-solutions-capabilities__card">
                                <div className="jk-solutions-capabilities__card-icon">
                                    {capability.icon}
                                </div>
                                <div className="jk-solutions-capabilities__card-metric">{capability.metric}</div>
                                <h3 className="jk-solutions-capabilities__card-title">{capability.title}</h3>
                                <p className="jk-solutions-capabilities__card-text">{capability.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernSpaceForce;
